import { useLazyQuery } from '@apollo/client';
import type {
  FetchPhysicalAddressQuery,
  FetchPhysicalAddressQueryVariables,
  SearchPhysicalAddressQuery,
  SearchPhysicalAddressQueryVariables,
} from '@generated/graphql';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import LockIcon from '@mui/icons-material/Lock';
import {
  type AutocompleteInputChangeReason,
  InputAdornment,
  type SxProps,
} from '@mui/material';
import type { CountryCodes, Jurisdictions } from '@onevesthq/ov-enums';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { getCountryName } from '@ovComponents/resources';
import { Tooltip, useToast } from 'hephaestus/web';
import { debounce, kebabCase } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { Autocomplete } from '../autocomplete/autocomplete';
import { FieldLabel, useFieldIds } from '../fieldLabel/fieldLabel';
import {
  FETCH_PHYSICAL_ADDRESS,
  SEARCH_PHYSICAL_ADDRESS,
} from './addressField.queries';
import { dedupeAddressOptionsByIdOrText } from './addressOption.utils';
import {
  ADDRESS_SEARCH_DEBOUNCE_MS,
  nextAddressSearchQuery,
  shouldCancelPendingAddressSearch,
  shouldRunAddressSearch,
} from './addressSearchConfig';
import { extractPostalCodeFromText, isCompletePostalCode } from './postalCode';

type Props = {
  label?: string;
  addressString?: string;
  setAddress: (address: Partial<PhysicalAddress>) => void;
  openManualInput: () => void;
  onClear?: () => void;
  integrationId?: string;
  organizationId?: string;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  autoFocus?: any;
  onFocus?: any;
  onBlur?: any;
  sx?: SxProps;
  infoTooltip?: string;
  addressCountry?: CountryCodes;
  allowedCountries: CountryCodes[];
  lockMessage?: string;
  testId?: string;
  onLookupFault?: () => void;
};

type PlaceType = {
  text: string;
  place_id?: string;
  id?: string;
};

const GoogleAddress = ({
  setAddress,
  openManualInput,
  onClear,
  label,
  addressString,
  addressCountry,
  error,
  integrationId,
  organizationId,
  errorText,
  disabled,
  onFocus,
  autoFocus,
  sx,
  onBlur,
  infoTooltip,
  allowedCountries,
  onLookupFault,
  lockMessage,
  testId,
}: Props): JSX.Element => {
  const { inputId, labelId } = useFieldIds(
    testId ? `${testId}-input` : undefined,
  );
  const [restrictedCountry, setRestrictedCountry] = useState<CountryCodes>(
    allowedCountries[0],
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const { t } = useTranslation(['pageConfiguration', 'components']);
  const { language } = useLocalization();
  const { enqueueToast } = useToast();
  const inputRef = useRef<any>(null);

  const [searchAddress] = useLazyQuery<
    SearchPhysicalAddressQuery,
    SearchPhysicalAddressQueryVariables
  >(SEARCH_PHYSICAL_ADDRESS);
  const [fetchAddress] = useLazyQuery<
    FetchPhysicalAddressQuery,
    FetchPhysicalAddressQueryVariables
  >(FETCH_PHYSICAL_ADDRESS);

  useEffect(() => {
    if (addressCountry && allowedCountries.includes(addressCountry)) {
      setRestrictedCountry(addressCountry);
    }
  }, [addressCountry, allowedCountries]);

  const lookupErrorMessage = t(
    'components:address.error.suggestionsUnavailable',
  );
  const handleLookupError = useCallback(
    (callback: (results?: readonly PlaceType[]) => void) => {
      enqueueToast(lookupErrorMessage, { variant: 'error' });
      if (onLookupFault) onLookupFault();
      callback([]);
    },
    [enqueueToast, onLookupFault, lookupErrorMessage],
  );

  const fetch = useMemo(
    () =>
      debounce(
        async (
          request: {
            input: string;
            countryCode: CountryCodes;
          },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          if (!integrationId || !organizationId) {
            handleLookupError(callback);
            return;
          }

          if (!shouldRunAddressSearch(request.input)) {
            callback([]);
            return;
          }

          try {
            const variables = {
              input: {
                integrationId,
                organizationId,
                searchTerm: request.input,
                countryCode: request.countryCode,
              },
              skipErrorHandler: true,
            };
            const { data } = await searchAddress({ variables });

            if (!data?.searchPhysicalAddress?.suggestions) {
              handleLookupError(callback);
              return;
            }

            const suggestions = data.searchPhysicalAddress.suggestions;
            const formattedSuggestions = suggestions.map((s) => ({
              text: s.text,
              place_id: s.id,
              id: s.id,
            }));
            callback(dedupeAddressOptionsByIdOrText(formattedSuggestions));
          } catch {
            handleLookupError(callback);
          }
        },
        ADDRESS_SEARCH_DEBOUNCE_MS,
      ),
    [integrationId, organizationId, searchAddress, handleLookupError],
  );

  // A lookup fault swaps to manual entry; drop any search still pending.
  useEffect(() => () => fetch.cancel(), [fetch]);

  useEffect(() => {
    let active = true;

    if (!searchQuery) {
      fetch.cancel();
      setOptions([]);
      return undefined;
    }

    if (!hasBeenFocused) return undefined;

    if (!shouldRunAddressSearch(searchQuery)) {
      fetch.cancel();
      setOptions([]);
      return undefined;
    }

    fetch(
      {
        input: searchQuery,
        countryCode: restrictedCountry,
      },
      (results?: readonly PlaceType[]) => {
        if (active) {
          setOptions(dedupeAddressOptionsByIdOrText(results ?? []));
        }
      },
    );
    return () => {
      active = false;
    };
  }, [searchQuery, fetch, restrictedCountry, hasBeenFocused]);

  if (!allowedCountries || allowedCountries.length === 0) return <></>;

  const handleSelect = async (
    placeId: string,
    selectedText: string,
  ): Promise<void> => {
    if (!integrationId || !organizationId) return;

    try {
      const { data } = await fetchAddress({
        variables: {
          input: {
            integrationId,
            organizationId,
            addressId: placeId,
          },
        },
      });

      const result = data?.fetchPhysicalAddress?.addressDetails;
      if (result) {
        const selectedPostalCode = extractPostalCodeFromText(selectedText);
        const normalizedPostalCode = isCompletePostalCode(
          result.postal ?? undefined,
        )
          ? (result.postal ?? undefined)
          : selectedPostalCode;

        const localAddress: Partial<PhysicalAddress> = {
          houseNumber: result.houseNumber ?? undefined,
          unitNumber: result.unitNumber ?? undefined,
          streetName: result.streetName ?? undefined,
          city: result.city ?? undefined,
          postal: normalizedPostalCode || undefined,
          jurisdiction: result.jurisdiction as Jurisdictions | undefined,
          country: result.country as CountryCodes | undefined,
          neighborhood: result.neighborhood ?? undefined,
        };
        setAddress(localAddress);
        openManualInput();
      }
    } catch {
      enqueueToast(
        'Failed to fetch address details. Fallback to manual input.',
        { variant: 'error' },
      );
    }
  };

  return (
    <>
      {label ? (
        <FieldLabel htmlFor={inputId} id={labelId} error={error}>
          {label}
        </FieldLabel>
      ) : null}
      {allowedCountries.length > 1 && (
        <RadioGroup
          name={testId ? `${testId}-country` : 'google-address-country'}
          testId={
            testId ? `${testId}-radio-group` : 'google-address-radio-group'
          }
          value={restrictedCountry}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            fetch.cancel();
            setSearchQuery('');
            setRestrictedCountry(e.target.value as CountryCodes);
            setAddress({});
          }}
        >
          {allowedCountries.map((cc) => (
            <Radio
              disabled={disabled}
              key={cc}
              value={cc}
              size='small'
              label={getCountryName(cc, language)}
              testId={
                testId
                  ? `${testId}-radio-option-${cc.toLowerCase()}`
                  : `google-address-radio-option-${cc}`
              }
            />
          ))}
        </RadioGroup>
      )}
      <Autocomplete
        id={inputId}
        testId={testId ?? 'google-address-autocomplete'}
        label=''
        infoTooltip={infoTooltip}
        error={error}
        errorText={
          errorText ?? t('components:fieldErrors.default', { fieldName: label })
        }
        disabled={disabled}
        onFocus={() => {
          setHasBeenFocused(true);
          if (onFocus) onFocus();
        }}
        autoFocus={autoFocus}
        blurOnSelect
        fullWidth
        disableClearable={!addressString}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.text
        }
        freeSolo
        onBlur={onBlur}
        sx={sx}
        InputProps={{
          inputRef,
          // Enabled: native clear-X (freeSolo hides the chevron itself).
          ...(disabled
            ? {
                endAdornment: (
                  <InputAdornment position='end'>
                    <Tooltip title={lockMessage ?? t('notEditableMessage')}>
                      <Icon icon={LockIcon} size='small' />
                    </Tooltip>
                  </InputAdornment>
                ),
              }
            : {}),
          disabled,
        }}
        options={[...options]}
        value={addressString}
        renderOption={(props: any, option: PlaceType) => (
          <li
            {...props}
            data-testid={
              testId
                ? `${testId}-${kebabCase(option.text)}`
                : `google-address-autocomplete-${kebabCase(option.text)}`
            }
          >
            {option.text}
          </li>
        )}
        onChange={(
          _: React.ChangeEvent<HTMLInputElement>,
          newValue: PlaceType | null,
        ) => {
          fetch.cancel();
          setSearchQuery('');
          setOptions((currentOptions) =>
            dedupeAddressOptionsByIdOrText(
              newValue ? [newValue, ...currentOptions] : currentOptions,
            ),
          );
          if (newValue) {
            const addressId = newValue.place_id || newValue.id;
            if (addressId) handleSelect(addressId, newValue.text);
          }
        }}
        onInputChange={(
          _: React.ChangeEvent<HTMLInputElement>,
          newInputValue: string,
          reason: AutocompleteInputChangeReason,
        ) => {
          if (shouldCancelPendingAddressSearch(reason)) {
            fetch.cancel();
          }
          setSearchQuery((current) =>
            nextAddressSearchQuery(current, newInputValue, reason),
          );
          if ((reason === 'input' || reason === 'clear') && !newInputValue) {
            if (onClear) onClear();
          }
        }}
        filterOptions={(x) => x}
      />
    </>
  );
};

export default GoogleAddress;
